import CoreBg from "@/assets/images/enfinity/core-bg.png";
import Twitch from "@/assets/images/task/twitch.png";
import Viralish from "@/assets/images/task/viralish.png";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import Button from "../ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";

export default function FeaturedCaseStudy() {
  const landingPage = import.meta.env.VITE_LANDING_PAGE;
  const { ref, isVisible } = useScrollReveal();
  const [currentIndex, setCurrentIndex] = useState(0);

  const reviews = [
    {
      title:
        "Viralish boosts creator operations with EnfiniTask, freeing 240+ hours a month and driving $3,500+ in new revenue",
      description:
        "Viralish turned to EnfiniTask for a more agile way to scale its multi-channel content business. By building a flexible team of certified managers, designers, and community specialists, they were able to offload repetitive tasks, manage 6 active channels seamlessly, and triple their outsourcing ROI — all while freeing up 8 hours a day for high-value growth work.",
      img: Viralish,
      link: "/case-study-1",
    },
    {
      title:
        "Top Twitch creator reclaims $30,000+ in monthly revenue by stopping piracy with EnfiniTask",
      description:
        "A leading Twitch gaming creator turned to EnfiniTask to protect their content from organized piracy channels. By leveraging a specialized team to monitor and strike down unauthorized uploads, they blocked 50M+ monthly piracy views before monetization, safeguarded their brand, and recovered over $30,000 in revenue each month — all while investing just $7,000 in on-demand talent.",
      img: Twitch,
      link: "/case-study-2",
    },
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  return (
    <div
      id="featured-case-studies"
      className={`${
        landingPage === "task" ? "lg:py-10" : "py-10 lg:py-32"
      } mx-auto 4xl:max-w-[100rem] relative overflow-hidden`}
    >
      {landingPage === "enfinity" && (
        <>
          <div
            className="absolute -top-[500px] lg:-top-[500px] -right-[550px] lg:-right-96 w-[800px] h-[800px] pointer-events-none z-0 hidden lg:block"
            style={{
              background:
                "radial-gradient(circle at center, rgba(109, 110, 208, 0.3), transparent 70%)",
            }}
          />
          <div
            className="absolute top-[450px] lg:-top-[500px] left-10 lg:-left-96 w-[800px] h-[800px] pointer-events-none z-0 hidden lg:block"
            style={{
              background:
                "radial-gradient(circle at center, rgba(114, 189, 196, 0.3), transparent 70%)",
            }}
          />
          <div
            className="absolute -top-[450px] lg:-top-[500px] left-1/2 -translate-x-1/2 w-[900px] h-[900px] pointer-events-none z-0"
            style={{
              background:
                "radial-gradient(circle at center, rgba(25, 106, 11, 0.3), transparent 70%)",
            }}
          />
        </>
      )}

      <div
        className={`space-y-12 lg:space-y-16 mx-auto ${
          landingPage === "enfinity" ? "lg:max-w-[61rem] xl:max-w-[75rem]" : ""
        }`}
      >
        {/* Header */}
        <div className="relative space-y-4 text-center px-8 lg:px-0 z-[2]">
          {landingPage === "task" ? (
            <h1 className="text-4xl md:text-5xl text-primaryLight z-40">
              Featured
              <br className="md:hidden" /> Case Studies
            </h1>
          ) : (
            <>
              <h2 className="flex justify-center text-4xl md:text-6xl font-medium text-white items-center gap-4 lg:hidden z-40">
                Featured case studies
              </h2>
              <h2 className="hidden lg:flex justify-center text-4xl md:text-6xl font-medium text-white items-center gap-4 z-40">
                <div className="relative">
                  <img
                    src={CoreBg}
                    className="absolute max-w-80 -top-[31px] -left-[75px]"
                    alt="Image"
                  />
                  Featured
                </div>
                case studies
              </h2>
            </>
          )}
          <p className="text-[#EDE9FE] max-w-3xl mx-auto text-sm md:text-lg z-40">
            Task helps content creators handle increasing demands by simplifying
            hiring and project management. With flexible support teams built
            through the platform, creators can streamline workflows and dedicate
            more energy to producing content.
          </p>
        </div>

        {/* Slideshow */}
        <div className="relative w-full flex justify-center items-center z-[2]">
          <button
            onClick={prevSlide}
            className="absolute left-2 md:left-10 p-3 rounded-full bg-black/50 hover:bg-black/70 transition z-10"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>

          <div className="w-[70%] md:w-[900px] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className={`rounded-[20px] border-t border-l border-r border-[#434242] bg-[#161616] p-8 md:p-12 space-y-8 relative`}
              >
                <h1
                  className={`text-2xl md:text-3xl max-w-xl text-left ${
                    landingPage === "task" ? "text-[#C4B5FD]" : "text-white"
                  }`}
                >
                  {reviews[currentIndex].title}
                </h1>
                <p className="text-white font-light text-sm md:text-base text-left">
                  {reviews[currentIndex].description}
                </p>

                <Link to={reviews[currentIndex].link} className="block">
                  <Button
                    className={`group relative rounded-full shadow text-white flex items-center justify-center gap-2 overflow-hidden hover:text-primary mx-auto lg:mx-0 ${
                      landingPage === "task"
                        ? "bg-primary hover:bg-white py-6 w-40 hover:text-primary"
                        : "bg-transparent border border-white py-5 w-44 hover:text-white"
                    }`}
                  >
                    <span className="transform transition-transform duration-300 group-hover:translate-x-5">
                      Read more
                    </span>
                    <ArrowRight className="transform transition-transform duration-300 group-hover:-translate-x-[80px]" />
                  </Button>
                </Link>

                <img
                  src={reviews[currentIndex].img}
                  className="max-w-40 absolute top-4 right-4 hidden md:block"
                  alt="Case Study"
                />
              </motion.div>
            </AnimatePresence>
          </div>

          <button
            onClick={nextSlide}
            className="absolute right-2 md:right-10 p-3 rounded-full bg-black/50 hover:bg-black/70 transition z-10"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
